import { useState, useCallback } from 'react';
import { apiClient, ApiClientError } from '@/api';

export interface CheckSlugResponse {
  slug: string;
  available: boolean;
  suggestion?: string | null;
}

export interface TenantInfo {
  id: string;
  name: string;
  slug: string;
  subdomain_url: string;
}

export interface UserInfo {
  id: string;
  email: string;
  name: string;
  role: string;
}

export interface RegisterResponse {
  tenant: TenantInfo;
  user: UserInfo;
}

export interface RegisterRequest {
  tenant_name: string;
  slug: string;
  country: string;
  registered_business_name: string;
  business_address: string;
  tax_id: string;
}

export interface UseRegistrationResult {
  checkSlug: (slug: string) => Promise<CheckSlugResponse>;
  register: (token: string, request: RegisterRequest) => Promise<RegisterResponse>;
  isCheckingSlug: boolean;
  isRegistering: boolean;
  error: ApiClientError | null;
  clearError: () => void;
}

export function useRegistration(): UseRegistrationResult {
  const [isCheckingSlug, setIsCheckingSlug] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [error, setError] = useState<ApiClientError | null>(null);

  const checkSlug = useCallback(async (slug: string): Promise<CheckSlugResponse> => {
    setIsCheckingSlug(true);
    setError(null);
    try {
      const response = await apiClient.get<CheckSlugResponse>(
        `/api/v1/register/check-slug?slug=${encodeURIComponent(slug)}`
      );
      return response;
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err);
      }
      throw err;
    } finally {
      setIsCheckingSlug(false);
    }
  }, []);

  const register = useCallback(
    async (token: string, request: RegisterRequest): Promise<RegisterResponse> => {
      setIsRegistering(true);
      setError(null);
      try {
        const response = await apiClient.post<RegisterResponse>(
          '/api/v1/register',
          request,
          { token }
        );
        return response;
      } catch (err) {
        if (err instanceof ApiClientError) {
          setError(err);
        }
        throw err;
      } finally {
        setIsRegistering(false);
      }
    },
    []
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    checkSlug,
    register,
    isCheckingSlug,
    isRegistering,
    error,
    clearError,
  };
}
